import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import TopBar from '@/components/feature/TopBar';
import ActionBar from '@/components/feature/ActionBar';
import MonthNav from '@/components/feature/MonthNav';
import { useStore } from '@/hooks/useStore';

function fmt(v: number) { return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }); }

const PAGAMENTO_ICONS: Record<string, string> = {
  dinheiro: 'ri-money-dollar-circle-line',
  pix: 'ri-qr-code-line',
  cartao: 'ri-bank-card-line',
};
const PAGAMENTO_COLORS: Record<string, string> = {
  dinheiro: 'text-green-400',
  pix: 'text-yellow-400',
  cartao: 'text-orange-400',
};

export default function VendasPage() {
  const { vendas, produtos, clientes, addVenda, updateVenda, deleteVenda, addCliente } = useStore();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ produtoId: '', quantidade: '', data: new Date().toISOString().split('T')[0], formaPagamento: 'pix' as 'dinheiro' | 'pix' | 'cartao', taxaCartao: '2.99', clienteId: '', descontoPercentual: '' });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [produtoSearch, setProdutoSearch] = useState('');
  const [showProdutoDropdown, setShowProdutoDropdown] = useState(false);
  const produtoDropdownRef = useRef<HTMLDivElement>(null);

  const [clienteSearch, setClienteSearch] = useState('');
  const [showClienteDropdown, setShowClienteDropdown] = useState(false);
  const clienteDropdownRef = useRef<HTMLDivElement>(null);

  const [showClienteModal, setShowClienteModal] = useState(false);
  const [clienteForm, setClienteForm] = useState({ nome: '', telefone: '' });

  const produtoSel = produtos.find(p => p.id === form.produtoId);
  const valorBruto = produtoSel ? produtoSel.precoVenda * Number(form.quantidade || 0) : 0;
  const descontoPercentual = Number(form.descontoPercentual || 0);
  const valorDesconto = valorBruto > 0 ? (valorBruto * descontoPercentual) / 100 : 0;
  const valorAposDesconto = valorBruto - valorDesconto;
  const taxaValor = form.formaPagamento === 'cartao' ? (valorAposDesconto * Number(form.taxaCartao || 0)) / 100 : 0;
  const valorLiquido = valorAposDesconto - taxaValor;

  const mesPad = String(mes).padStart(2, '0');
  const anoStr = String(ano);

  const vendasFiltradas = vendas.filter(v => {
    if (!v.data) return true;
    const [a, m] = v.data.split('-');
    return a === anoStr && m === mesPad;
  });

  const totalVendidoFiltrado = vendasFiltradas.reduce((acc, v) => acc + v.valorLiquido, 0);
  const totalBrutoFiltrado = vendasFiltradas.reduce((acc, v) => acc + v.valorTotal, 0);
  const totalTaxasFiltrado = vendasFiltradas.reduce((acc, v) => acc + (v.valorTotal - v.valorLiquido - (v.valorDesconto ?? 0)), 0);
  const totalDescontosFiltrado = vendasFiltradas.reduce((acc, v) => acc + (v.valorDesconto ?? 0), 0);
  const custoVendasFiltrado = vendasFiltradas.reduce((acc, v) => {
    const produto = produtos.find(p => p.id === v.produtoId);
    const custoUnitario = produto ? produto.custo + produto.custoExtra : 0;
    return acc + (custoUnitario * v.quantidade);
  }, 0);
  const lucroFiltrado = totalBrutoFiltrado - custoVendasFiltrado - totalDescontosFiltrado;

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (clienteDropdownRef.current && !clienteDropdownRef.current.contains(event.target as Node)) {
        setShowClienteDropdown(false);
      }
      if (produtoDropdownRef.current && !produtoDropdownRef.current.contains(event.target as Node)) {
        setShowProdutoDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function openNew() { setEditingId(null); setForm({ produtoId: '', quantidade: '', data: new Date().toISOString().split('T')[0], formaPagamento: 'pix', taxaCartao: '2.99', clienteId: '', descontoPercentual: '' }); setClienteSearch(''); setProdutoSearch(''); setShowClienteDropdown(false); setShowProdutoDropdown(false); setClienteForm({ nome: '', telefone: '' }); setShowModal(true); }
  function openEdit(v: typeof vendas[0]) { setEditingId(v.id); setForm({ produtoId: v.produtoId, quantidade: String(v.quantidade), data: v.data, formaPagamento: v.formaPagamento, taxaCartao: String(v.taxaCartao), clienteId: v.clienteId ?? '', descontoPercentual: v.descontoPercentual ? String(v.descontoPercentual) : '' }); setClienteSearch(clientes.find(c => c.id === v.clienteId)?.nome ?? ''); setProdutoSearch(produtos.find(p => p.id === v.produtoId)?.nome ?? ''); setShowClienteDropdown(false); setShowProdutoDropdown(false); setClienteForm({ nome: '', telefone: '' }); setShowModal(true); }

  function handleSaveCliente() {
    if (!clienteForm.nome.trim()) return;
    addCliente({ nome: clienteForm.nome.trim(), telefone: clienteForm.telefone.trim() });
    const novoNome = clienteForm.nome.trim();
    setShowClienteModal(false);
    setClienteForm({ nome: '', telefone: '' });
    setClienteSearch(novoNome);
    setShowClienteDropdown(true);
  }

  function handleSave() {
    if (!form.produtoId || !form.quantidade || !form.data) return;
    const clienteSel = clientes.find(c => c.id === form.clienteId);
    if (editingId) {
      updateVenda(editingId, {
        produtoId: form.produtoId,
        produtoNome: produtoSel?.nome ?? '',
        quantidade: Number(form.quantidade),
        valorTotal: valorBruto,
        formaPagamento: form.formaPagamento,
        taxaCartao: form.formaPagamento === 'cartao' ? Number(form.taxaCartao) : 0,
        valorLiquido,
        data: form.data,
        clienteId: form.clienteId || undefined,
        clienteNome: clienteSel?.nome ?? undefined,
        descontoPercentual: descontoPercentual > 0 ? descontoPercentual : undefined,
        valorDesconto: valorDesconto > 0 ? valorDesconto : undefined,
      });
    } else {
      addVenda({ produtoId: form.produtoId, produtoNome: produtoSel!.nome, quantidade: Number(form.quantidade), valorTotal: valorBruto, formaPagamento: form.formaPagamento, taxaCartao: form.formaPagamento === 'cartao' ? Number(form.taxaCartao) : 0, valorLiquido, data: form.data, clienteId: form.clienteId || undefined, clienteNome: clienteSel?.nome ?? undefined, descontoPercentual: descontoPercentual > 0 ? descontoPercentual : undefined, valorDesconto: valorDesconto > 0 ? valorDesconto : undefined });
    }
    setShowModal(false);
    setEditingId(null);
    setClienteSearch('');
    setProdutoSearch('');
    setClienteForm({ nome: '', telefone: '' });
    setForm({ produtoId: '', quantidade: '', data: new Date().toISOString().split('T')[0], formaPagamento: 'pix', taxaCartao: '2.99', clienteId: '', descontoPercentual: '' });
  }

  return (
    <AppLayout>
      <TopBar title="Vendas" subtitle="Registre e acompanhe suas vendas" />
      <ActionBar>
        <div className="flex flex-col gap-2 w-full">
          <MonthNav mes={mes} ano={ano} onChange={(m, a) => { setMes(m); setAno(a); }} />
          <div className="flex gap-2">
            <button onClick={openNew} className="btn-gold flex-1 flex items-center justify-center gap-2 text-sm font-bold py-2.5 rounded-xl cursor-pointer whitespace-nowrap">
              <i className="ri-add-line text-base"></i> Nova Venda
            </button>
            <button onClick={() => navigate('/clientes')} className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap md:hidden">
              <i className="ri-user-3-line text-base"></i> Gerenciar Clientes
            </button>
          </div>
        </div>
      </ActionBar>
      <div className="pt-[196px] md:pt-[196px] p-3 md:p-6 space-y-3 animate-fade-in-up">
        {/* Lucro Liquido */}
        <div className={`rounded-xl p-3 md:p-4 border flex flex-col items-center justify-center text-center ${lucroFiltrado >= 0 ? 'bg-neon/5 border-neon/30' : 'bg-red-500/5 border-red-500/30'}`}>
          <p className="text-[10px] md:text-xs text-dark-muted uppercase tracking-wider mb-1">Lucro Liquido do Mes</p>
          <p className={`text-xl md:text-3xl font-bold font-mono ${lucroFiltrado >= 0 ? 'text-neon' : 'text-red-400'}`}>{fmt(lucroFiltrado)}</p>
          <p className="text-[10px] md:text-xs text-dark-muted mt-1">{fmt(totalBrutoFiltrado)} - {fmt(custoVendasFiltrado)} - {fmt(totalDescontosFiltrado)}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Liquido Recebido</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-neon">{fmt(totalVendidoFiltrado)}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Bruto Vendido</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-dark-text">{fmt(totalBrutoFiltrado)}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Taxas Pagas</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-red-400">{fmt(totalTaxasFiltrado)}</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-3 md:p-4 flex flex-col items-center text-center">
            <p className="text-[10px] text-dark-muted uppercase tracking-wider mb-1">Descontos</p>
            <p className="text-lg md:text-2xl font-bold font-mono text-orange-400">{fmt(totalDescontosFiltrado)}</p>
          </div>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {vendasFiltradas.length === 0 ? (
            <div className="bg-dark-card border border-dark-border rounded-xl p-8 flex flex-col items-center text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card2 mb-2"><i className="ri-shopping-bag-3-line text-xl text-dark-muted"></i></div>
              <p className="text-sm text-dark-muted">Nenhuma venda neste mes</p>
            </div>
          ) : vendasFiltradas.map(v => (
            <div key={v.id} className="bg-dark-card border border-dark-border rounded-xl p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-dark-text text-sm truncate">{v.produtoNome}</p>
                  <p className="text-xs text-dark-muted mt-0.5">{new Date(v.data).toLocaleDateString('pt-BR')}</p>
                  {v.clienteNome && (
                    <div className="flex items-center gap-1 mt-1">
                      <div className="w-3 h-3 flex items-center justify-center"><i className="ri-user-3-line text-[10px] text-neon"></i></div>
                      <p className="text-[10px] text-neon font-medium">{v.clienteNome}</p>
                    </div>
                  )}
                </div>
                <div className="flex gap-1 ml-2 shrink-0">
                  <button onClick={() => openEdit(v)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-dark-card2 text-dark-muted hover:text-neon cursor-pointer"><i className="ri-edit-line text-sm"></i></button>
                  <button onClick={() => setConfirmDelete(v.id)} className="w-7 h-7 flex items-center justify-center rounded-lg bg-dark-card2 text-dark-muted hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-dark-card2 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-dark-muted uppercase mb-0.5">Qtd</p>
                  <p className="text-xs font-bold font-mono text-dark-text">{v.quantidade}x</p>
                </div>
                <div className="bg-dark-card2 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-dark-muted uppercase mb-0.5">Pagamento</p>
                  <div className="flex items-center justify-center gap-1">
                    <div className="w-3 h-3 flex items-center justify-center"><i className={`${PAGAMENTO_ICONS[v.formaPagamento]} text-xs ${PAGAMENTO_COLORS[v.formaPagamento]}`}></i></div>
                    <p className="text-xs font-bold text-dark-text capitalize">{v.formaPagamento}</p>
                  </div>
                </div>
                <div className="bg-neon/8 border border-neon/20 rounded-lg p-2 text-center">
                  <p className="text-[9px] text-dark-muted uppercase mb-0.5">Liquido</p>
                  <p className="text-xs font-bold font-mono text-neon">{fmt(v.valorLiquido)}</p>
                </div>
              </div>
              <div className="space-y-1 mt-2 px-1">
                {(v.valorDesconto ?? 0) > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-dark-muted">Bruto: <span className="line-through">{fmt(v.valorTotal)}</span></span>
                    <span className="text-[10px] text-orange-400">desconto -{v.descontoPercentual}% ({fmt(v.valorDesconto!)})</span>
                  </div>
                )}
                {v.taxaCartao > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-dark-muted">Apos desconto: <span className="text-dark-text">{fmt(v.valorTotal - (v.valorDesconto ?? 0))}</span></span>
                    <span className="text-[10px] text-red-400">taxa -{v.taxaCartao}% ({fmt(v.valorTotal - v.valorLiquido - (v.valorDesconto ?? 0))})</span>
                  </div>
                )}
                {(v.valorDesconto ?? 0) === 0 && v.taxaCartao === 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-dark-muted">Bruto</span>
                    <span className="text-[10px] text-dark-text font-mono">{fmt(v.valorTotal)}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full table-dark">
              <thead><tr><th className="text-left">Produto</th><th className="text-left">Cliente</th><th className="text-left">Qtd</th><th className="text-left">Pagamento</th><th className="text-left">Bruto</th><th className="text-left">Desconto</th><th className="text-left">Taxa</th><th className="text-left">Liquido</th><th className="text-left">Data</th><th className="text-left">Acoes</th></tr></thead>
              <tbody>
                {vendasFiltradas.map(v => (
                  <tr key={v.id}>
                    <td className="font-medium">{v.produtoNome}</td>
                    <td>
                      {v.clienteNome ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-user-3-line text-xs text-neon"></i></div>
                          <span className="text-xs text-dark-muted">{v.clienteNome}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-dark-muted opacity-40">-</span>
                      )}
                    </td>
                    <td className="text-dark-muted">{v.quantidade}x</td>
                    <td><div className="flex items-center gap-1.5"><div className="w-4 h-4 flex items-center justify-center"><i className={`${PAGAMENTO_ICONS[v.formaPagamento]} text-sm ${PAGAMENTO_COLORS[v.formaPagamento]}`}></i></div><span className="text-xs capitalize text-dark-muted">{v.formaPagamento}</span></div></td>
                    <td className="font-mono text-sm text-dark-muted">{fmt(v.valorTotal)}</td>
                    <td className="text-xs text-orange-400">{(v.valorDesconto ?? 0) > 0 ? `-${fmt(v.valorDesconto!)}` : '-'}</td>
                    <td className="text-xs text-red-400">{v.taxaCartao > 0 ? `-${fmt(v.valorTotal - v.valorLiquido - (v.valorDesconto ?? 0))}` : '-'}</td>
                    <td className="text-neon font-mono font-bold">{fmt(v.valorLiquido)}</td>
                    <td className="text-dark-muted">{new Date(v.data).toLocaleDateString('pt-BR')}</td>
                    <td><div className="flex gap-1"><button onClick={() => openEdit(v)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted hover:text-neon cursor-pointer"><i className="ri-edit-line text-sm"></i></button><button onClick={() => setConfirmDelete(v.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-dark-muted hover:text-red-400 cursor-pointer"><i className="ri-delete-bin-line text-sm"></i></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Venda */}
      {showModal && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up max-h-[88vh] overflow-y-auto shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">{editingId ? 'Editar Venda' : 'Nova Venda'}</h2>
              <button onClick={() => { setShowModal(false); setClienteSearch(''); setProdutoSearch(''); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>
            <div className="space-y-3">
              <div className="relative" ref={produtoDropdownRef}>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Produto</label>
                <input
                  type="text"
                  className="input-dark w-full"
                  placeholder="Buscar produto por nome..."
                  value={produtoSearch}
                  onChange={e => { setProdutoSearch(e.target.value); setShowProdutoDropdown(true); }}
                  onFocus={() => setShowProdutoDropdown(true)}
                />
                {showProdutoDropdown && (
                  <div className="absolute z-10 w-full bg-dark-card2 border border-dark-border rounded-xl mt-1 max-h-52 overflow-y-auto shadow-lg">
                    <div
                      onClick={() => { setForm(f => ({ ...f, produtoId: '' })); setProdutoSearch(''); setShowProdutoDropdown(false); }}
                      className={`px-3 py-2 cursor-pointer text-sm hover:bg-neon/10 ${!form.produtoId ? 'text-neon font-medium' : 'text-dark-muted'}`}
                    >
                      Selecione o produto...
                    </div>
                    {produtos
                      .filter(p => p.status === 'ativo' && p.estoque > 0 && p.nome.toLowerCase().includes(produtoSearch.toLowerCase()))
                      .map(p => (
                        <div
                          key={p.id}
                          onClick={() => { setForm(f => ({ ...f, produtoId: p.id })); setProdutoSearch(p.nome); setShowProdutoDropdown(false); }}
                          className={`px-3 py-2 cursor-pointer text-sm hover:bg-neon/10 ${form.produtoId === p.id ? 'text-neon font-medium' : 'text-dark-text'}`}
                        >
                          {p.nome} - {fmt(p.precoVenda)} (Estoque: {p.estoque})
                        </div>
                      ))}
                    {produtos.filter(p => p.status === 'ativo' && p.estoque > 0 && p.nome.toLowerCase().includes(produtoSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs text-dark-muted">
                        Nenhum produto encontrado. <a href="/produtos" className="text-neon hover:underline" onClick={() => setShowModal(false)}>Cadastre um</a>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="relative" ref={clienteDropdownRef}>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Cliente <span className="font-normal opacity-60">(opcional)</span></label>
                <div className="relative">
                  <input
                    type="text"
                    className="input-dark w-full pr-10"
                    placeholder="Buscar cliente por nome..."
                    value={clienteSearch}
                    onChange={e => { setClienteSearch(e.target.value); setShowClienteDropdown(true); }}
                    onFocus={() => setShowClienteDropdown(true)}
                  />
                  <button
                    onClick={() => { setShowClienteModal(true); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-neon/10 text-neon cursor-pointer transition-colors"
                    title="Cadastrar novo cliente"
                  >
                    <i className="ri-add-line text-lg font-bold"></i>
                  </button>
                </div>
                {showClienteDropdown && (
                  <div className="absolute z-10 w-full bg-dark-card2 border border-dark-border rounded-xl mt-1 max-h-40 overflow-y-auto shadow-lg">
                    <div
                      onClick={() => { setForm(f => ({ ...f, clienteId: '' })); setClienteSearch(''); setShowClienteDropdown(false); }}
                      className={`px-3 py-2 cursor-pointer text-sm hover:bg-neon/10 ${!form.clienteId ? 'text-neon font-medium' : 'text-dark-muted'}`}
                    >
                      Sem cliente
                    </div>
                    {clientes
                      .filter(c => c.nome.toLowerCase().includes(clienteSearch.toLowerCase()))
                      .map(c => (
                        <div
                          key={c.id}
                          onClick={() => { setForm(f => ({ ...f, clienteId: c.id })); setClienteSearch(c.nome); setShowClienteDropdown(false); }}
                          className={`px-3 py-2 cursor-pointer text-sm hover:bg-neon/10 ${form.clienteId === c.id ? 'text-neon font-medium' : 'text-dark-text'}`}
                        >
                          {c.nome}
                        </div>
                      ))}
                    {clientes.filter(c => c.nome.toLowerCase().includes(clienteSearch.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs text-dark-muted">
                        Nenhum cliente encontrado.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-dark-muted mb-1 block font-medium">Quantidade</label><input type="number" className="input-dark" placeholder="0" min="1" max={produtoSel?.estoque} value={form.quantidade} onChange={e => setForm(f => ({ ...f, quantidade: e.target.value }))} /></div>
                <div><label className="text-xs text-dark-muted mb-1 block font-medium">Data</label><input type="date" className="input-dark" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} /></div>
              </div>
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Desconto (%)</label>
                <div className="relative"><input type="number" className="input-dark pr-8" placeholder="0" min="0" max="100" step="0.01" value={form.descontoPercentual} onChange={e => setForm(f => ({ ...f, descontoPercentual: e.target.value }))} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm font-bold">%</span></div>
              </div>
              <div><label className="text-xs text-dark-muted mb-2 block font-medium">Forma de Pagamento</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['dinheiro', 'pix', 'cartao'] as const).map(fp => (
                    <button key={fp} onClick={() => setForm(f => ({ ...f, formaPagamento: fp }))} className={`flex flex-col items-center gap-1 py-3 rounded-xl cursor-pointer transition-all border ${form.formaPagamento === fp ? 'bg-neon/10 border-neon/40 text-neon' : 'bg-dark-card2 border-dark-border text-dark-muted hover:text-dark-text'}`}>
                      <div className="w-5 h-5 flex items-center justify-center"><i className={`${PAGAMENTO_ICONS[fp]} text-lg`}></i></div>
                      <span className="text-[10px] font-semibold capitalize">{fp}</span>
                    </button>
                  ))}
                </div>
              </div>
              {form.formaPagamento === 'cartao' && (
                <div><label className="text-xs text-dark-muted mb-1 block font-medium">Taxa da maquininha (%)</label>
                  <div className="relative"><input type="number" className="input-dark pr-8" placeholder="2.99" step="0.01" value={form.taxaCartao} onChange={e => setForm(f => ({ ...f, taxaCartao: e.target.value }))} /><span className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-muted text-sm font-bold">%</span></div>
                </div>
              )}
              {valorBruto > 0 && (
                <div className="bg-neon/5 border border-neon/20 rounded-xl p-3 space-y-1.5">
                  <div className="flex justify-between items-center"><span className="text-xs text-dark-muted">Valor bruto</span><span className="text-sm font-bold font-mono text-dark-text">{fmt(valorBruto)}</span></div>
                  {valorDesconto > 0 && <div className="flex justify-between items-center"><span className="text-xs text-dark-muted">Desconto</span><span className="text-sm font-bold font-mono text-orange-400">-{fmt(valorDesconto)}</span></div>}
                  <div className="flex justify-between items-center"><span className="text-xs text-dark-muted">Apos desconto</span><span className="text-sm font-bold font-mono text-dark-text">{fmt(valorAposDesconto)}</span></div>
                  {taxaValor > 0 && <div className="flex justify-between items-center"><span className="text-xs text-dark-muted">Taxa maquininha</span><span className="text-sm font-bold font-mono text-red-400">-{fmt(taxaValor)}</span></div>}
                  <div className="flex justify-between items-center border-t border-dark-border pt-1.5"><span className="text-xs font-semibold text-dark-text">Voce recebe</span><span className="text-lg font-bold font-mono text-neon">{fmt(valorLiquido)}</span></div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowModal(false); setClienteSearch(''); setProdutoSearch(''); }} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSave} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">{editingId ? 'Salvar' : 'Registrar Venda'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Cadastrar Cliente (dentro do modal de venda) */}
      {showClienteModal && (
        <div className="fixed inset-0 z-[60] modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up shadow-[0_0_40px_rgba(201,168,76,0.07)]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-dark-text">Novo Cliente</h2>
              <button onClick={() => { setShowClienteModal(false); setClienteForm({ nome: '', telefone: '' }); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"><i className="ri-close-line text-lg"></i></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Nome completo</label>
                <input
                  type="text"
                  className="input-dark"
                  placeholder="Ex: Maria Silva"
                  value={clienteForm.nome}
                  onChange={e => setClienteForm(f => ({ ...f, nome: e.target.value }))}
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs text-dark-muted mb-1 block font-medium">Telefone</label>
                <input
                  type="tel"
                  className="input-dark"
                  placeholder="(11) 98765-4321"
                  value={clienteForm.telefone}
                  onChange={e => setClienteForm(f => ({ ...f, telefone: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setShowClienteModal(false); setClienteForm({ nome: '', telefone: '' }); }} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={handleSaveCliente} className="btn-gold flex-1 py-3 rounded-xl text-sm cursor-pointer whitespace-nowrap">Cadastrar</button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
          <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-sm p-6 animate-fade-in-up">
            <h3 className="text-center text-base font-bold text-dark-text mb-2">Excluir venda?</h3>
            <p className="text-center text-sm text-dark-muted mb-5">Esta acao nao pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-3 rounded-xl border border-dark-border text-dark-muted text-sm cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => { deleteVenda(confirmDelete); setConfirmDelete(null); }} className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm cursor-pointer whitespace-nowrap">Excluir</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}